<template>
  <div class="ps-stamp-card">
    <div class="ps-stamp-card__header">
      <div>
        <div class="ps-stamp-card__eyebrow">
          {{ $t("stamp_modal_subtitle") }}
        </div>
        <div class="ps-stamp-card__count">
          {{ stampStatus.stamp_count }} /
          {{ stampStatus.stamps_required_to_redeem }}
        </div>
      </div>

      <v-chip
        v-if="stampStatus.eligible"
        small
        class="ps-stamp-card__chip"
        text-color="white"
      >
        <v-icon left x-small>fal fa-gift</v-icon>
        {{ $t("stamp_reward_ready_one") }}
      </v-chip>
    </div>

    <v-progress-linear
      :value="clampedProgress"
      height="8"
      rounded
      class="ps-stamp-progress"
    />

    <div class="ps-stamp-slots">
      <div
        v-for="i in stampStatus.stamps_required_to_redeem"
        :key="i"
        class="ps-stamp-slot"
        :class="{ filled: i <= stampStatus.stamp_count }"
      >
        <v-icon v-if="i <= stampStatus.stamp_count" x-small>
          fal fa-check
        </v-icon>
      </div>
    </div>

    <div v-if="extraStamps > 0" class="ps-stamp-card__overflow">
      <v-icon x-small color="green darken-2">fal fa-plus-circle</v-icon>
      {{ $t("stamp_extra_after_threshold", { n: extraStamps }) }}
    </div>

    <div class="ps-stamp-card__footer">
      <v-icon x-small color="amber darken-2">fal fa-sparkles</v-icon>
      {{
        $t("stamp_total_earned_lifetime", {
          n: stampStatus.total_stamps_earned,
        })
      }}
    </div>
  </div>
</template>

<script>
export default {
  name: "StampCard",
  props: {
    stampStatus: { type: Object, required: true },
  },
  computed: {
    clampedProgress() {
      const required = Number(this.stampStatus.stamps_required_to_redeem) || 0;
      const count = Number(this.stampStatus.stamp_count) || 0;
      if (required <= 0) return 0;
      const pct = (count / required) * 100;
      return Math.min(100, Math.max(0, pct));
    },
    extraStamps() {
      const required = Number(this.stampStatus.stamps_required_to_redeem) || 0;
      const count = Number(this.stampStatus.stamp_count) || 0;
      return Math.max(0, count - required);
    },
  },
};
</script>

<style scoped>
.ps-stamp-card {
  --stamp-primary: #5b39c9;
  --stamp-primary-dark: #4527a8;
  --stamp-amber: #f59e0b;
  --stamp-ink: #0f172a;
  --stamp-muted: #64748b;
  --stamp-border: rgba(91, 57, 201, 0.12);

  position: relative;
  overflow: hidden;
  padding: 18px;
  border: 1px solid var(--stamp-border);
  border-radius: 18px;
  background: linear-gradient(135deg, #ffffff 0%, #faf8ff 52%, #fff7ed 100%);
}

.ps-stamp-card::before {
  content: "";
  position: absolute;
  top: -42px;
  right: -38px;
  width: 110px;
  height: 110px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.16);
}

.ps-stamp-card__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.ps-stamp-card__eyebrow {
  color: var(--stamp-muted);
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.4;
}

.ps-stamp-card__count {
  color: var(--stamp-ink);
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.1;
}

.ps-stamp-card__chip {
  background: linear-gradient(135deg, #16a34a, #22c55e) !important;
  box-shadow: 0 8px 18px rgba(34, 197, 94, 0.24);
}

.ps-stamp-progress {
  overflow: hidden;
  margin-bottom: 16px;
  background: rgba(91, 57, 201, 0.1) !important;
}

.ps-stamp-progress ::v-deep .v-progress-linear__determinate {
  background: linear-gradient(90deg, var(--stamp-primary), var(--stamp-amber)) !important;
}

.ps-stamp-slots {
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(32px, 1fr));
  gap: 8px;
  margin-bottom: 14px;
}

.ps-stamp-slot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 0 auto;
  border-radius: 50%;
  border: 1.5px dashed rgba(91, 57, 201, 0.28);
  background: rgba(255, 255, 255, 0.72);
  box-shadow: inset 0 0 0 3px rgba(91, 57, 201, 0.04);
  transition: background-color 180ms ease, border-color 180ms ease, transform 180ms ease;
}

.ps-stamp-slot .v-icon {
  color: #ffffff;
}

.ps-stamp-slot.filled {
  border-style: solid;
  border-color: rgba(91, 57, 201, 0.16);
  background: linear-gradient(135deg, var(--stamp-primary), var(--stamp-primary-dark));
  box-shadow: 0 8px 16px rgba(91, 57, 201, 0.22);
}

.ps-stamp-card__overflow {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(34, 197, 94, 0.12);
  color: #15803d;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.ps-stamp-card__footer {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--stamp-muted);
  font-size: 0.78rem;
  font-weight: 600;
}

@media (max-width: 600px) {
  .ps-stamp-card {
    padding: 16px;
    border-radius: 16px;
  }

  .ps-stamp-card__count {
    font-size: 1.75rem;
  }
}
</style>

